import { useState, useEffect, useRef, useCallback } from 'react';
import { API } from '../../api';

export const POLL_INTERVAL_MS = 3000;
// Tiene que ser mayor que TALENTSENSE_AI_TIMEOUT del backend (600s por default):
// el backend recién escribe el desenlace cuando su propia llamada corta, así que
// un cliente más impaciente que el servidor avisaría "no respondió" sobre una
// clasificación que sigue viva. El corte existe igual porque el worker es un
// thread daemon y un reinicio de gunicorn deja la fila pendiente para siempre.
export const POLL_TIMEOUT_MS = 12 * 60 * 1000;
// Un GET fallido no es motivo para abandonar: en una ventana de minutos se
// pierde alguno durante un deploy o un hipo de nginx. Sólo varios seguidos
// significan que el backend no está.
export const MAX_POLL_FAILURES = 5;

export default function useAIClassification(replyID) {
    // El historial completo: la primera es la más reciente (el backend ordena
    // por -created_date) y las demás son las corridas con otros specs, que es
    // lo que la pantalla compara.
    const [classifications, setClassifications] = useState([]);
    const [starting, setStarting] = useState(false);
    const [error, setError] = useState('');
    const [errorDetail, setErrorDetail] = useState('');
    const [timedOut, setTimedOut] = useState(false);

    const timerRef = useRef(null);
    const deadlineRef = useRef(null);
    const mountedRef = useRef(true);
    const failuresRef = useRef(0);
    // El polling se reagenda a sí mismo; el ref evita que `poll` se referencie
    // dentro de su propia definición.
    const pollRef = useRef(null);

    const stopPolling = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        deadlineRef.current = null;
    }, []);

    // Siempre limpia el timer anterior: montaje y clic pueden encolar cada uno
    // el suyo, y sin esto quedarían dos cadenas de polling corriendo en paralelo.
    // El vencimiento se chequea acá y no en la respuesta, para que también corte
    // cuando lo que se está encadenando son reintentos fallidos.
    const scheduleNextPoll = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        if (deadlineRef.current && Date.now() > deadlineRef.current) {
            stopPolling();
            setTimedOut(true);
            return;
        }
        timerRef.current = setTimeout(() => pollRef.current?.(), POLL_INTERVAL_MS);
    }, [stopPolling]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    const poll = useCallback(() => {
        API.get(`/reply/${replyID}/classify/`)
            .then((response) => {
                if (!mountedRef.current) return;
                failuresRef.current = 0;
                const results = response.data?.results ?? [];
                setClassifications(results);
                // Lista vacía: todavía no se clasificó nunca este reply.
                const latest = results[0];
                if (!latest) return;
                if (latest.status !== 'P') {
                    stopPolling();
                    return;
                }
                scheduleNextPoll();
            })
            .catch(() => {
                if (!mountedRef.current) return;
                // Abandonar al primer error dejaría la tarjeta con el spinner
                // girando para siempre, porque la fila sigue en 'P' y nadie más
                // vuelve a preguntar. Se reintenta y recién se avisa al final.
                failuresRef.current += 1;
                if (failuresRef.current >= MAX_POLL_FAILURES) {
                    stopPolling();
                    setError('ai_error');
                    return;
                }
                scheduleNextPoll();
            });
    }, [replyID, stopPolling, scheduleNextPoll]);

    useEffect(() => {
        pollRef.current = poll;
    }, [poll]);

    // Carga inicial: si al entrar hay una clasificación pendiente (otra pestaña,
    // o un refresh), se retoma el polling donde estaba.
    useEffect(() => {
        if (!replyID) return;
        deadlineRef.current = Date.now() + POLL_TIMEOUT_MS;
        failuresRef.current = 0;
        poll();
        return stopPolling;
    }, [replyID, poll, stopPolling]);

    // `specName` vacío deja que el backend use el default del panel.
    const classify = useCallback((specName = '') => {
        setStarting(true);
        setError('');
        setErrorDetail('');
        setTimedOut(false);
        failuresRef.current = 0;
        API.post(`/reply/${replyID}/classify/`, { spec_name: specName })
            .then((response) => {
                if (!mountedRef.current) return;
                // La corrida nueva es la más reciente y encabeza el historial;
                // el próximo GET lo confirma, pero así la card reacciona ya.
                setClassifications((prev) => [response.data, ...prev]);
                deadlineRef.current = Date.now() + POLL_TIMEOUT_MS;
                scheduleNextPoll();
            })
            .catch((err) => {
                if (!mountedRef.current) return;
                const status = err?.response?.status;
                if (status === 409) {
                    // Ya había una corriendo: engancharse a esa en vez de fallar.
                    deadlineRef.current = Date.now() + POLL_TIMEOUT_MS;
                    poll();
                    return;
                }
                // El 400 del spec inválido trae el motivo del servicio, que es
                // más útil que un "algo falló" genérico.
                setErrorDetail(status === 400 ? (err?.response?.data?.detail ?? '') : '');
                setError(status === 503 ? 'ai_unavailable' : 'ai_error');
            })
            .finally(() => {
                if (mountedRef.current) setStarting(false);
            });
    }, [replyID, poll, scheduleNextPoll]);

    const classification = classifications[0] ?? null;

    return {
        classification,
        classifications,
        classify,
        starting,
        error,
        errorDetail,
        timedOut,
        // Sin `!error` el botón quedaría deshabilitado con el spinner girando
        // después de abandonar el polling: la fila sigue diciendo 'P' pero ya
        // nadie está mirando, así que el operador tiene que poder reintentar.
        pending: classification?.status === 'P' && !timedOut && !error,
    };
}
